import type { Token } from '@kudos/http-client'
import { useRollbarPerson } from '@rollbar/react'
import { createContext, useContext, useMemo, type JSX } from 'react'
import { useAuthStore } from '../globals/stores/useAuthStore'

export type AuthContextType = {
  isLoading: boolean
  isAuthenticated: boolean
  tenantId: string | null
  userId: string | null
  isTenantScope: boolean
  isUserScope: boolean
  session: string | null
  initialize: (accessToken: Token, options?: { persist?: boolean; skipMe?: boolean }) => void
  initializeFromStorage: () => Promise<void>
  clearAuth: () => void
  clearStorage: () => void
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export const AuthProvider = ({ children }: { children: JSX.Element }) => {
  const {
    session,
    tenantId,
    userId,
    tenantMemberId,
    isLoading,
    initialize,
    initializeFromStorage,
    clearAuth: clearAuthInternal,
    clearStorage,
  } = useAuthStore()

  const memoedValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated: Boolean(session),
      isTenantScope: Boolean(tenantId),
      isUserScope: Boolean(session) && !tenantId,
      session,
      tenantId,
      userId,
      tenantMemberId,
      clearStorage,
      clearAuth: () => clearAuthInternal(true),
      initialize,
      initializeFromStorage,
    }),
    [
      session,
      tenantId,
      userId,
      tenantMemberId,
      isLoading,
      initialize,
      initializeFromStorage,
      clearAuthInternal,
      clearStorage,
    ],
  )
  useRollbarPerson({
    userId: memoedValue.userId,
    tenantId: memoedValue.tenantId,
    tenantMemberId: memoedValue.tenantMemberId,
  })

  return <AuthContext.Provider value={memoedValue}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  return useContext(AuthContext)
}
